import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  History,
  ChevronRight,
  ChevronDown,
  FileText,
  Users,
  Target,
  Archive,
  MoreHorizontal,
} from 'lucide-react'
import { cn } from '@goodday-clone/ui'
import { cardClassName } from '@jira-clone/ui'

interface ProjectNode {
  id: string
  label: string
  count: number
  icon: React.ReactElement | any
  children?: ProjectNode[]
  href?: string
  expanded?: boolean
}

interface ActivityEntry {
  id: string
  type: 'deal' | 'lead' | 'note' | 'task' | 'email' | 'call'
  title: string
  description: string
  time: string
  user: string
  userAvatar?: string
  module: string
  moduleHref: string
}

interface DateGroup {
  label: string
  date: string
  isToday: boolean
  activities: ActivityEntry[]
}

const ACTIVITY_TYPES = {
  deal: { icon: Target, color: 'text-primary', bg: 'bg-primary/10' },
  lead: { icon: Users, color: 'text-green-600', bg: 'bg-green-100' },
  note: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-100' },
  task: { icon: MoreHorizontal, color: 'text-blue-600', bg: 'bg-blue-100' },
  email: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-100' },
  call: { icon: Users, color: 'text-orange-600', bg: 'bg-orange-100' },
}

const projectTree: ProjectNode[] = [
  {
    id: 'sales',
    label: 'Sales',
    count: 7,
    icon: <Target className="w-4 h-4 text-primary" />,
    expanded: true,
    children: [
      {
        id: 'deals',
        label: 'Deals',
        count: 3,
        icon: <Target className="w-4 h-4 text-primary" />,
        expanded: true,
        children: [
          { id: 'active-deals', label: 'Active Deals', count: 3, icon: <Target className="w-4 h-4 text-primary" />, href: '/app/deals?status=active' },
        ],
      },
      {
        id: 'leads',
        label: 'Leads',
        count: 4,
        icon: <Users className="w-4 h-4 text-green-600" />,
        expanded: true,
        children: [
          { id: 'all-leads', label: 'All Leads', count: 1, icon: <Users className="w-4 h-4 text-green-600" />, href: '/app/leads' },
          { id: 'archive', label: 'Archive', count: 3, icon: <Archive className="w-4 h-4 text-muted-foreground" />, href: '/app/leads?status=archived' },
        ],
      },
    ],
  },
]

const dateGroups: DateGroup[] = [
  {
    label: 'Today',
    date: 'Sep 16',
    isToday: true,
    activities: [
      {
        id: '1',
        type: 'deal',
        title: 'New deal created: Enterprise License',
        description: 'Deal worth ₹50,000 created for Acme Corp',
        time: '2 hours ago',
        user: 'Sarah Chen',
        module: 'Deals',
        moduleHref: '/app/deals',
      },
      {
        id: '2',
        type: 'lead',
        title: 'Lead qualified: TechStart Inc',
        description: 'Moved from New to Qualified stage',
        time: '4 hours ago',
        user: 'Mike Johnson',
        module: 'Leads',
        moduleHref: '/app/leads',
      },
      {
        id: '3',
        type: 'note',
        title: 'Note added to deal: Global Contract',
        description: 'Discussed pricing terms with legal team',
        time: '6 hours ago',
        user: 'Emily Davis',
        module: 'Deals',
        moduleHref: '/app/deals',
      },
    ],
  },
  {
    label: 'Yesterday',
    date: 'Sep 15',
    isToday: false,
    activities: [
      {
        id: '4',
        type: 'deal',
        title: 'Deal won: Startup Package',
        description: 'Closed deal worth ₹15,000 with InnovateLabs',
        time: 'Yesterday, 4:30 PM',
        user: 'Sarah Chen',
        module: 'Deals',
        moduleHref: '/app/deals',
      },
      {
        id: '5',
        type: 'lead',
        title: 'New lead: DataFlow Systems',
        description: 'Inbound inquiry from website contact form',
        time: 'Yesterday, 2:15 PM',
        user: 'Mike Johnson',
        module: 'Leads',
        moduleHref: '/app/leads',
      },
      {
        id: '6',
        type: 'note',
        title: 'Client meeting recap: DataFlow Systems',
        description: 'Reviewed initial requirements and implementation timeline',
        time: 'Yesterday, 11:30 AM',
        user: 'Emily Davis',
        module: 'Notes',
        moduleHref: '/app/leads',
      },
    ],
  },
  {
    label: 'Sep 13',
    date: 'Sep 13',
    isToday: false,
    activities: [
      {
        id: '7',
        type: 'email',
        title: 'Email sent: Proposal follow-up',
        description: 'Sent proposal to Acme Corp decision makers',
        time: 'Sep 13, 5:45 PM',
        user: 'Sarah Chen',
        module: 'Email',
        moduleHref: '/app/deals',
      },
      {
        id: '8',
        type: 'call',
        title: 'Call completed: Discovery call',
        description: '30 min discovery call with TechStart Inc',
        time: 'Sep 13, 10:00 AM',
        user: 'Mike Johnson',
        module: 'Calls',
        moduleHref: '/app/leads',
      },
    ],
  },
]

function ProjectTreeItem({
  node,
  level = 0,
  onToggle,
  onNavigate,
}: {
  node: ProjectNode
  level?: number
  onToggle: (id: string) => void
  onNavigate?: (href: string) => void
}) {
  const hasChildren = Boolean(node.children && node.children.length > 0)
  const isExpanded = node.expanded ?? true

  return (
    <div className="pl-2">
      <button
        onClick={() => {
          if (hasChildren) {
            onToggle(node.id)
          } else if (node.href && onNavigate) {
            onNavigate(node.href)
          }
        }}
        className={cn(
          'w-full flex items-center gap-2 px-2 py-1.5 rounded-md',
          'hover:bg-accent text-sm font-medium',
          'transition-colors duration-150',
          level > 0 && 'text-muted-foreground',
          level === 0 && 'text-foreground font-semibold'
        )}
        style={{ paddingLeft: `${12 + level * 16}px` }}
      >
        {hasChildren && (
          <ChevronDown
            className={cn(
              'w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform duration-150',
              isExpanded && 'rotate-90'
            )}
          />
        )}
        {!hasChildren && <span className="w-4 h-4 flex-shrink-0" />}
        {node.icon}
        <span className="flex-1 truncate text-left">{node.label}</span>
        <span
          className={cn(
            'text-xs font-medium px-2 py-0.5 rounded-full',
            level === 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
          )}
        >
          {node.count}
        </span>
      </button>
      {hasChildren && isExpanded && (
        <div className="mt-1 space-y-1 border-l border-border ml-4">
          {node.children!.map((child) => (
            <ProjectTreeItem
              key={child.id}
              node={child}
              level={level + 1}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AllProjectsPanel({ onNavigate }: { onNavigate: (href: string) => void }) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['sales', 'deals', 'leads']))

  const toggleNode = (id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const renderTree = () => {
    return projectTree.map((node) => (
      <ProjectTreeItem
        key={node.id}
        node={{ ...node, expanded: expandedNodes.has(node.id) }}
        onToggle={toggleNode}
        onNavigate={onNavigate}
      />
    ))
  }

  return (
    <aside className="w-72 lg:w-80 flex-shrink-0 border-l border-border bg-sidebar-background p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-sidebar-foreground">All Projects</h2>
        <button className="p-1.5 rounded hover:bg-sidebar-accent transition-colors" aria-label="More options">
          <MoreHorizontal className="w-4 h-4 text-sidebar-foreground" />
        </button>
      </div>
      <nav className="space-y-1" role="navigation" aria-label="Project navigation">
        {renderTree()}
      </nav>
    </aside>
  )
}

function Breadcrumb() {
  const items = [
    { label: 'Home', href: '/app' },
    { label: 'Recent', href: '/app/recent', current: true },
  ]

  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
      <ol className="flex items-center gap-1">
        {items.map((item, index) => (
          <li key={item.label} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" aria-hidden="true" />
            )}
            {item.current ? (
              <span className="text-foreground font-medium">{item.label}</span>
            ) : (
              <Link to={item.href} className="hover:text-foreground transition-colors">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

function ActivityEntryComponent({
  activity,
  isLast = false,
  onNavigate,
}: {
  activity: ActivityEntry
  isLast?: boolean
  onNavigate?: (href: string) => void
}) {
  const { icon: Icon, color, bg } = (ACTIVITY_TYPES as any)[activity.type] || ACTIVITY_TYPES.task

  return (
    <div className="relative pl-10 pb-6 last:pb-0">
      <div className="absolute left-3 top-1">
        <div
          className={cn(
            'w-6 h-6 rounded-full border-2 flex items-center justify-center',
            'bg-background',
            activity.type === 'deal' ? 'border-primary' : 'border-muted'
          )}
        >
          <Icon className={cn('w-3 h-3', color)} />
        </div>
        {!isLast && <div className="absolute left-5 top-7 bottom-0 w-0.5 bg-border" />}
      </div>
      <div className={cn(cardClassName({ interactive: true }), 'p-4 transition-shadow')}>
        <div className="flex items-start gap-3">
          <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0', bg)}>
            <Icon className={cn('w-4 h-4', color)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-foreground truncate">{activity.title}</h4>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{activity.time}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-2">{activity.description}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>By {activity.user}</span>
              <span
                className="text-primary hover:underline cursor-pointer"
                onClick={() => onNavigate && onNavigate(activity.moduleHref)}
              >
                {activity.module}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function DateGroupSection({
  group,
  onNavigate,
}: {
  group: DateGroup
  onNavigate?: (href: string) => void
}) {
  return (
    <section className="mb-8" aria-labelledby={`date-${group.label.toLowerCase()}`}>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-end min-w-[80px] pr-4 pt-1">
          <span className="text-xs font-semibold text-foreground text-right">{group.label}</span>
          <span className="text-xs text-muted-foreground text-right">{group.date}</span>
        </div>
        <div className="relative flex-1">
          <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-border" />
          <div
            className={cn(
              'absolute left-0 top-1 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-background z-10',
              group.isToday ? 'border-primary' : 'border-muted'
            )}
          >
            {group.isToday && <div className="w-2 h-2 rounded-full bg-primary" />}
          </div>
          <div className="pl-10 space-y-4">
            {group.activities.map((activity, index) => (
              <ActivityEntryComponent
                key={activity.id}
                activity={activity}
                isLast={index === group.activities.length - 1}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default function RecentPage() {
  const navigate = useNavigate()

  const handleNavigate = (href: string) => {
    navigate(href)
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
        <div className="w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <History className="w-5 h-5" aria-hidden="true" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Recent</h1>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-8">
          <main className="flex-1 min-w-0 lg:pr-8">
            <Breadcrumb />
            <div className="space-y-2" role="feed" aria-label="Recent activity timeline">
              {dateGroups.map((group) => (
                <DateGroupSection key={group.label} group={group} onNavigate={handleNavigate} />
              ))}
            </div>
          </main>
          <AllProjectsPanel onNavigate={handleNavigate} />
        </div>
      </div>
    </div>
  )
}
